import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowForward, AttachFile } from "@mui/icons-material";
import EnhancedTable from "@/shared/views/EnhancedTable";
import ApiService from "@/api/ApiService";
import { usePersistantStore } from "@/stores/global";
import useRights from "@/shared/api/useRights";
import { User } from "@/types/types";
import dayjs from "dayjs";
import { GiftIcon, Search } from "lucide-react";
import { DirectStoffstromEditor } from "@/components/stoffstrom/DirectStoffstromEditor";
import { Fragment } from "react/jsx-runtime";

export default function Stoffstrom() {
  const query = useQuery<any[], AxiosResponse>({
    queryKey: ["myStoffstrom"],
    queryFn: ApiService.getStoffstromEntriesByUser(),
  });

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        overflow: "auto",
        flexDirection: "column",
      }}
    >
      {!query.data || query.data?.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, alignSelf: "center", color: "#444", flex: 1 }}>
          <div>Nichts gefunden</div>
        </Box>
      ) : (
        query.data
          ?.sort((a, b) => a.project.number.localeCompare(b.project.number) || a.type.localeCompare(b.type))
          .map((y, i, f) => (
            <Fragment key={y.id}>
              {i === 0 || f[i - 1].project.number !== y.project.number ? (
                <div className="notificationDate">
                  <span style={{ fontSize: "10px", marginRight: "2px" }}>{y.project.number}</span>
                  {y.project.name}
                </div>
              ) : null}
              <Box
                sx={{
                  background: "white",

                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid #777",
                  mt: "6px",
                  mx: "6px",
                  alignItems: "stretch",
                  overflow: "hidden",
                  borderRadius: "5px",
                  flexShrink: 0,
                  fontSize: "13px",
                }}
              >
                <Box sx={{ display: "flex", borderBottom: "1px solid #777" }}>
                  <Box
                    sx={{
                      borderRight: "1px solid #aaa",
                      color: "#333",
                      boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                      p: "4px 6px",
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    style={y.type === "OFFER" ? { background: "#dcfbd4" } : { background: "rgb(251, 212, 243)" }}
                  >
                    {y.type === "OFFER" ? <GiftIcon fontSize="inherit" /> : <Search fontSize="inherit" />}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: "4px",
                      overflow: "hidden",
                      lineHeight: 1,
                      fontWeight: "bold",
                      alignSelf: "center",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {y.category.name}
                  </Box>
                  <Box
                    sx={{
                      borderLeft: "1px solid #aaa",
                      color: "#333",
                      boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                      p: "4px 6px",
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    style={y.type === "OFFER" ? { background: "#dcfbd4" } : { background: "rgb(251, 212, 243)" }}
                  >
                    {y.amount}
                    {/[a-zA-Z]/.test(y.amount) ? "" : " t"}
                  </Box>
                </Box>
                <Box
                  sx={{
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    p: "3px",
                    backgroundColor: "#eee",
                  }}
                >
                  {!!y.date
                    ? y.date
                    : "von " +
                      dayjs(y.validFrom || y.project.plannedStart).format("DD.MM.YYYY") +
                      " bis " +
                      dayjs(y.validTo || y.project.plannedEnd).format("DD.MM.YYYY")}
                </Box>
                {!!y.comment ? (
                  <Box
                    sx={{
                      p: "2px",
                      pt: "3px",
                      borderTop: "1px solid #aaa",

                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "12px",
                      lineHeight: 1.1,
                      backgroundColor: "#f7f7f7",
                      boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                    }}
                  >
                    {y.comment}
                  </Box>
                ) : null}{" "}
                {!!y.reservationComment ? (
                  <Box
                    sx={{
                      p: "2px",
                      pt: "3px",
                      borderTop: "1px solid #aaa",

                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "12px",
                      lineHeight: 1.1,
                      backgroundColor: "#f7f7f7",
                      boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                    }}
                  >
                    {y.reservationComment}
                  </Box>
                ) : null}
                {y.attachments.map((att) => (
                  <Box
                    component="a"
                    href={(import.meta.env.VITE_APP_URL + "/image/") + att.filename}
                    target="_blank"
                    sx={{
                      p: "2px",
                      pt: "3px",
                      borderTop: "1px solid #aaa",
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.5,
                      },
                      color: "#444",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "12px",
                      lineHeight: 1.1,
                      backgroundColor: "#f7f7f7",
                      boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                    }}
                  >
                    <AttachFile sx={{ mr: "2px", verticalAlign: "text-top" }} fontSize="inherit" />
                    {att.originalName}
                  </Box>
                ))}
                <Box
                  sx={{
                    p: "2px",
                    pt: "3px",
                    borderTop: "1px solid #aaa",

                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "12px",
                    lineHeight: 1.1,
                    backgroundColor: "#f7f7f7",
                    boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                  }}
                >
                  Erstellt am {dayjs.utc(y.createdAt).local().format("DD.MM.YYYY [um] HH:mm [Uhr]")}
                </Box>
                {true ? (
                  <Box
                    sx={{
                      p: "2px",
                      pt: "3px",
                      borderTop: "1px solid #aaa",
                      justifyContent: "flex-end",
                      display: "flex",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "12px",
                      lineHeight: 1.1,
                      backgroundColor: "#f7f7f7",
                      boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                    }}
                  >
                    <DirectStoffstromEditor
                      order={{ ...y, category: y.category.id }}
                      plannedStart={y.project.plannedStart}
                      plannedEnd={y.project.plannedEnd}
                    />
                  </Box>
                ) : null}
              </Box>
            </Fragment>
          ))
      )}
    </div>
  );
}
